import {
  zipSync,
} from "fflate"
import {
  ArrowLeft,
  CircleHelp,
  Printer,
} from "lucide-react"
import {
  startTransition,
  useEffect,
  useEffectEvent,
  useMemo,
  useRef,
  useState,
} from "react"

import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import {
  apiClient,
  type B2cStockBatchDetail,
  type B2cStockPhysicalTagRecord,
} from "@/lib/api/client"
import { getSafetyQrVariant } from "@/lib/safetyqr-catalog"
import {
  OperatorStatusMessage,
} from "@/features/operator/shared/operator-shell"
import { titleizeToken } from "@/features/operator/shared/operator-access"

declare global {
  interface Window {
    QRCode?: QRCodeLibrary
  }
}

type QRCodeInstance = {
  clear?: () => void
}

type QRCodeConstructor = new (
  element: HTMLElement,
  options: {
    colorDark?: string
    colorLight?: string
    correctLevel?: unknown
    height?: number
    text: string
    width?: number
  }
) => QRCodeInstance

type QRCodeLibrary = QRCodeConstructor & {
  CorrectLevel?: {
    M?: unknown
  }
}

interface AdminStockPrintSearch {
  autoPrint?: boolean
  batchId?: string
  returnTo?: string
}

interface AdminStockPrintPreviewProps {
  search: AdminStockPrintSearch
}

type StickerCategoryKey =
  | "car"
  | "elderly"
  | "fallback"
  | "plantations"
  | "rider"
  | "student"
  | "women"

type StickerCategoryDescriptor = {
  badgeClassName: string
  exportBadgeFill: string
  exportIconColor: string
  label: string
  svgMarkup: string
}

type StickerPlacementKey =
  | "dashboard"
  | "fallback"
  | "helmet"
  | "phone_back_cover"
  | "windshield"

let qrCodeLibraryPromise: Promise<void> | null = null

// The export artboard is 900x1120 px, which maps to 3.00" x 3.73" at 300 DPI.
const STICKER_EXPORT_WIDTH_PX = 900
const STICKER_EXPORT_HEIGHT_PX = 1120
const STICKER_EXPORT_JPEG_QUALITY = 0.95
const STICKER_EXPORT_TITLE_FONT_SIZE_PX = 44
const STICKER_EXPORT_TAG_ID_FONT_SIZE_PX = 42
const STICKER_EXPORT_HOST_FONT_SIZE_PX = 18

const STICKER_CATEGORY_DESCRIPTORS: Record<StickerCategoryKey, StickerCategoryDescriptor> = {
  car: {
    badgeClassName: "border-slate-200 bg-slate-100 text-slate-700",
    exportBadgeFill: "#e2e8f0",
    exportIconColor: "#334155",
    label: "Car",
    svgMarkup: `
      <path d="M5 15.5h14" />
      <path d="M7 15.5V17a1 1 0 0 0 1 1h1" />
      <path d="M17 15.5V17a1 1 0 0 1-1 1h-1" />
      <path d="M6 15.5l1.4-4.1A2 2 0 0 1 9.3 10h5.4a2 2 0 0 1 1.9 1.4L18 15.5" />
      <circle cx="8.5" cy="15.5" r="1.5" />
      <circle cx="15.5" cy="15.5" r="1.5" />
    `.trim(),
  },
  rider: {
    badgeClassName: "border-sky-200 bg-sky-100 text-sky-700",
    exportBadgeFill: "#e0f2fe",
    exportIconColor: "#0369a1",
    label: "Two Wheeler",
    svgMarkup: `
      <circle cx="6" cy="16.5" r="3" />
      <circle cx="18" cy="16.5" r="3" />
      <path d="M6 16.5l4-8h4" />
      <path d="M10 8.5l4 8" />
      <path d="M10 16.5h8" />
      <path d="M14 8.5h3" />
    `.trim(),
  },
  women: {
    badgeClassName: "border-rose-200 bg-rose-100 text-rose-700",
    exportBadgeFill: "#ffe4e6",
    exportIconColor: "#be185d",
    label: "Women",
    svgMarkup: `
      <circle cx="12" cy="6.5" r="2.5" />
      <path d="M9 12l3-2 3 2" />
      <path d="M12 10v4" />
      <path d="M8.5 14.5h7" />
      <path d="M9 20l1.5-5h3L15 20" />
    `.trim(),
  },
  student: {
    badgeClassName: "border-violet-200 bg-violet-100 text-violet-700",
    exportBadgeFill: "#ede9fe",
    exportIconColor: "#6d28d9",
    label: "Student",
    svgMarkup: `
      <path d="M3 9.5l9-4 9 4-9 4-9-4Z" />
      <path d="M7 11.5v4.5c0 1.1 2.2 2 5 2s5-.9 5-2v-4.5" />
      <path d="M20 10.5v5" />
    `.trim(),
  },
  elderly: {
    badgeClassName: "border-amber-200 bg-amber-100 text-amber-700",
    exportBadgeFill: "#fef3c7",
    exportIconColor: "#b45309",
    label: "Elderly",
    svgMarkup: `
      <circle cx="10" cy="6.5" r="2.5" />
      <path d="M10 9v4.5" />
      <path d="M8 13l2-2 3 1" />
      <path d="M8.5 20l1.5-5" />
      <path d="M11.5 20l1.5-4.5" />
      <path d="M17 10v10" />
      <path d="M17 20h2" />
    `.trim(),
  },
  plantations: {
    badgeClassName: "border-emerald-200 bg-emerald-100 text-emerald-700",
    exportBadgeFill: "#d1fae5",
    exportIconColor: "#047857",
    label: "Plantations",
    svgMarkup: `
      <path d="M12 4l4 5h-2l3 4h-3l2 3H8l2-3H7l3-4H8l4-5Z" />
      <path d="M12 16v4" />
      <path d="M10 20h4" />
    `.trim(),
  },
  fallback: {
    badgeClassName: "border-slate-200 bg-slate-100 text-slate-600",
    exportBadgeFill: "#e2e8f0",
    exportIconColor: "#475569",
    label: "SafetyQR",
    svgMarkup: `
      <circle cx="12" cy="12" r="8" />
      <path d="M9.75 9.5a2.25 2.25 0 1 1 3.5 1.9c-.75.5-1.25 1.05-1.25 1.85" />
      <circle cx="12" cy="16.5" r="0.7" fill="currentColor" stroke="none" />
    `.trim(),
  },
}

const STICKER_PLACEMENT_DESCRIPTORS: Record<
  StickerPlacementKey,
  StickerCategoryDescriptor
> = {
  windshield: {
    badgeClassName: "border-emerald-200 bg-emerald-100 text-emerald-700",
    exportBadgeFill: "#d1fae5",
    exportIconColor: "#047857",
    label: "Windshield",
    svgMarkup: `
      <path d="M5 16.5h14" />
      <path d="M7.5 16.5v1.5" />
      <path d="M16.5 16.5v1.5" />
      <path d="M7 16.5l1.2-5.2A2 2 0 0 1 10.15 9.8h3.7a2 2 0 0 1 1.95 1.5L17 16.5" />
      <path d="M9.5 10.5l-1 6" />
      <path d="M14.5 10.5l1 6" />
    `.trim(),
  },
  dashboard: {
    badgeClassName: "border-sky-200 bg-sky-100 text-sky-700",
    exportBadgeFill: "#e0f2fe",
    exportIconColor: "#0369a1",
    label: "Dashboard",
    svgMarkup: `
      <path d="M4.5 16.5h15" />
      <path d="M7 16.5c.7-3.8 3-6 5-6s4.3 2.2 5 6" />
      <path d="M12 12.5v2.5" />
      <path d="M12 12.5l3-1.2" />
    `.trim(),
  },
  helmet: {
    badgeClassName: "border-emerald-200 bg-emerald-100 text-emerald-700",
    exportBadgeFill: "#d1fae5",
    exportIconColor: "#047857",
    label: "Helmet",
    svgMarkup: `
      <path d="M6 14.5a6 6 0 1 1 12 0v1H6Z" />
      <path d="M18 15.5h-2.5a2.5 2.5 0 0 0-2.5 2.5" />
      <path d="M9 18h4" />
    `.trim(),
  },
  phone_back_cover: {
    badgeClassName: "border-sky-200 bg-sky-100 text-sky-700",
    exportBadgeFill: "#e0f2fe",
    exportIconColor: "#0369a1",
    label: "Phone Back Cover",
    svgMarkup: `
      <rect x="7" y="3.5" width="10" height="17" rx="2.5" />
      <circle cx="12" cy="16.5" r="0.8" fill="currentColor" stroke="none" />
      <circle cx="12" cy="6.8" r="0.9" />
    `.trim(),
  },
  fallback: {
    badgeClassName: "border-slate-200 bg-slate-100 text-slate-600",
    exportBadgeFill: "#e2e8f0",
    exportIconColor: "#475569",
    label: "Sticker",
    svgMarkup: `
      <rect x="5" y="5" width="14" height="14" rx="3" />
      <path d="M9 9h6" />
      <path d="M9 12h6" />
      <path d="M9 15h4" />
    `.trim(),
  },
}

function formatDateTime(value: string | null | undefined) {
  if (!value) {
    return "Not recorded"
  }

  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return value
  }

  return parsed.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}

function getVariantLabel(batch: B2cStockBatchDetail | null) {
  return batch?.kitVariant
    ? getSafetyQrVariant(batch.kitVariant)?.displayName ?? titleizeToken(batch.kitVariant)
    : "SafetyQR print preview"
}

function getStickerTheme(tag: B2cStockPhysicalTagRecord) {
  if (tag.role?.includes("incident")) {
    return {
      cardClass: "border-amber-200",
      footerClass: "bg-amber-50",
      iconClass: "bg-amber-100 text-amber-800",
      qrColor: "#b45309",
      qrFrameClass: "border-amber-200/80",
      titleClass: "text-amber-900",
    }
  }

  if (tag.role?.includes("zone_tag") || tag.iconVariant === "alert") {
    return {
      cardClass: "border-indigo-200",
      footerClass: "bg-indigo-50",
      iconClass: "bg-indigo-100 text-indigo-800",
      qrColor: "#4338ca",
      qrFrameClass: "border-indigo-200/80",
      titleClass: "text-indigo-950",
    }
  }

  if (tag.role?.includes("secondary") || tag.iconVariant === "phone") {
    return {
      cardClass: "border-sky-200",
      footerClass: "bg-sky-50",
      iconClass: "bg-sky-100 text-sky-800",
      qrColor: "#1d5f99",
      qrFrameClass: "border-sky-200/80",
      titleClass: "text-sky-950",
    }
  }

  return {
    cardClass: "border-emerald-200",
    footerClass: "bg-emerald-50",
    iconClass: "bg-emerald-100 text-emerald-800",
    qrColor: "#0f766e",
    qrFrameClass: "border-emerald-200/80",
    titleClass: "text-emerald-950",
  }
}

function estimateQrPixelSize(sizeMm: B2cStockPhysicalTagRecord["sizeMm"]) {
  const longestSide = Math.max(sizeMm?.width ?? 35, sizeMm?.height ?? 35)
  return Math.max(220, Math.round(longestSide * 6.4))
}

function getTileMinHeight(sizeMm: B2cStockPhysicalTagRecord["sizeMm"]) {
  const longestSide = Math.max(sizeMm?.width ?? 35, sizeMm?.height ?? 35)
  return `${Math.max(320, Math.round(longestSide * 7.2))}px`
}

function getStickerCategory(
  batch: B2cStockBatchDetail,
  kit: B2cStockBatchDetail["kits"][number]
): StickerCategoryDescriptor {
  const variant = kit.kitVariant ?? batch.kitVariant ?? ""

  switch (variant) {
    case "car_safety_kit":
      return STICKER_CATEGORY_DESCRIPTORS.car
    case "riders_safety_kit":
      return STICKER_CATEGORY_DESCRIPTORS.rider
    case "women_safety_qr":
      return STICKER_CATEGORY_DESCRIPTORS.women
    case "students_safety_qr":
      return STICKER_CATEGORY_DESCRIPTORS.student
    case "elderly_safety_qr":
      return STICKER_CATEGORY_DESCRIPTORS.elderly
    case "plantations_safety_qr":
      return STICKER_CATEGORY_DESCRIPTORS.plantations
  }

  if (kit.vehicleClass === "four_wheeler") {
    return STICKER_CATEGORY_DESCRIPTORS.car
  }

  if (kit.vehicleClass === "two_wheeler") {
    return STICKER_CATEGORY_DESCRIPTORS.rider
  }

  return STICKER_CATEGORY_DESCRIPTORS.fallback
}

function sanitizeFileSegment(value: string) {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || "sticker"
}

function escapeSvgText(value: string) {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&apos;")
}

function getStickerFooterHost(tag: B2cStockPhysicalTagRecord) {
  if (!tag.qrTargetUrl) {
    return "traffic.feedbacknfc.com"
  }

  try {
    return new URL(tag.qrTargetUrl).hostname
  } catch {
    return "traffic.feedbacknfc.com"
  }
}

function getStickerPlacementDescriptor(
  tag: B2cStockPhysicalTagRecord,
  category: StickerCategoryDescriptor
) {
  const normalizedPlacement = (tag.placement ?? "").trim().toLowerCase()

  if (tag.iconVariant === "helmet" || normalizedPlacement.includes("helmet")) {
    return STICKER_PLACEMENT_DESCRIPTORS.helmet
  }

  if (
    tag.iconVariant === "phone"
    || normalizedPlacement.includes("phone case back")
    || normalizedPlacement.includes("phone back cover")
  ) {
    return STICKER_PLACEMENT_DESCRIPTORS.phone_back_cover
  }

  if (normalizedPlacement.includes("windshield")) {
    return STICKER_PLACEMENT_DESCRIPTORS.windshield
  }

  if (normalizedPlacement.includes("dashboard")) {
    return STICKER_PLACEMENT_DESCRIPTORS.dashboard
  }

  return category
}

function hasDistinctPlacementDescriptor(
  category: StickerCategoryDescriptor,
  placement: StickerCategoryDescriptor
) {
  return placement.label !== category.label
}

function downloadBlob(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement("a")
  anchor.href = url
  anchor.download = fileName
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  URL.revokeObjectURL(url)
}

function StickerCategoryBadge(props: { category: StickerCategoryDescriptor }) {
  return (
    <span
      aria-label={`Category icon: ${props.category.label}`}
      className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border ${props.category.badgeClassName}`}
      role="img"
    >
      <svg
        aria-hidden="true"
        className="h-4 w-4"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.8"
        viewBox="0 0 24 24"
        dangerouslySetInnerHTML={{ __html: props.category.svgMarkup }}
      />
    </span>
  )
}

function StickerPlacementBadge(props: { descriptor: StickerCategoryDescriptor }) {
  return (
    <span
      aria-label={`Placement icon: ${props.descriptor.label}`}
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] ${props.descriptor.badgeClassName}`}
    >
      <svg
        aria-hidden="true"
        className="h-3.5 w-3.5"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.8"
        viewBox="0 0 24 24"
        dangerouslySetInnerHTML={{ __html: props.descriptor.svgMarkup }}
      />
      <span>{props.descriptor.label}</span>
    </span>
  )
}

async function renderQrCodeDataUrl(tag: B2cStockPhysicalTagRecord, colorDark: string) {
  if (!tag.qrTargetUrl) {
    throw new Error(`Missing QR target for ${tag.tag}`)
  }

  await loadQrCodeLibrary()

  if (typeof window.QRCode !== "function") {
    throw new Error("Unable to load QRCode renderer")
  }

  const container = document.createElement("div")
  container.style.position = "fixed"
  container.style.left = "-9999px"
  container.style.top = "0"
  container.style.width = "512px"
  container.style.height = "512px"
  document.body.appendChild(container)

  try {
    new window.QRCode(container, {
      text: tag.qrTargetUrl,
      width: 512,
      height: 512,
      colorDark,
      colorLight: "#ffffff",
      correctLevel: window.QRCode.CorrectLevel?.M,
    })

    const canvas = container.querySelector("canvas")
    if (canvas instanceof HTMLCanvasElement) {
      return canvas.toDataURL("image/png")
    }

    const image = container.querySelector("img")
    if (image instanceof HTMLImageElement && image.src) {
      return image.src
    }

    const svg = container.querySelector("svg")
    if (svg instanceof SVGElement) {
      const svgMarkup = new XMLSerializer().serializeToString(svg)
      return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgMarkup)}`
    }

    throw new Error(`QR renderer did not produce an exportable asset for ${tag.tag}`)
  } finally {
    container.remove()
  }
}

async function buildStickerSvg(
  tag: B2cStockPhysicalTagRecord,
  category: StickerCategoryDescriptor
) {
  const theme = getStickerTheme(tag)
  const placement = getStickerPlacementDescriptor(tag, category)
  const showPlacement = hasDistinctPlacementDescriptor(category, placement)
  const qrDataUrl = await renderQrCodeDataUrl(tag, theme.qrColor)
  const title = escapeSvgText(tag.primaryCopy ?? "SCAN TO HELP")
  const tagId = escapeSvgText(tag.tag)
  const host = escapeSvgText(getStickerFooterHost(tag))
  const qrImageHref = escapeSvgText(qrDataUrl)
  const categoryLabel = escapeSvgText(category.label)
  const placementLabel = escapeSvgText(placement.label)
  const categoryPillWidth = Math.max(200, Math.min(320, 96 + category.label.length * 16))
  const placementPillWidth = Math.max(220, Math.min(320, 108 + placement.label.length * 10))
  const placementPillX = 34 + categoryPillWidth + 18
  const metadataHeight = showPlacement ? 188 : 168
  const metadataY = 1120 - metadataHeight - 24
  const categoryTextX = 34 + 72
  const tagIdY = showPlacement ? 1046 : 1036
  const hostY = showPlacement ? 1082 : 1070

  return `
    <svg xmlns="http://www.w3.org/2000/svg" width="${STICKER_EXPORT_WIDTH_PX}" height="${STICKER_EXPORT_HEIGHT_PX}" viewBox="0 0 ${STICKER_EXPORT_WIDTH_PX} ${STICKER_EXPORT_HEIGHT_PX}" role="img" aria-label="${tagId}">
      <rect x="2" y="2" width="896" height="1116" rx="40" fill="#ffffff" stroke="#dbe5f1" stroke-width="4" />
      <text x="34" y="68" fill="#0f172a" font-family="Inter, Arial, sans-serif" font-size="${STICKER_EXPORT_TITLE_FONT_SIZE_PX}" font-weight="500">${title}</text>
      <rect x="70" y="148" width="760" height="760" rx="24" fill="#ffffff" />
      <image href="${qrImageHref}" x="70" y="148" width="760" height="760" preserveAspectRatio="xMidYMid meet" />
      <rect x="22" y="${metadataY}" width="856" height="${metadataHeight}" rx="32" fill="#f8fafc" />
      <rect x="34" y="${metadataY + 20}" width="${categoryPillWidth}" height="56" rx="28" fill="${category.exportBadgeFill}" />
      <g transform="translate(50 ${metadataY + 36})" fill="none" stroke="${category.exportIconColor}" color="${category.exportIconColor}" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" aria-label="${categoryLabel}">
        ${category.svgMarkup}
      </g>
      <text x="${categoryTextX}" y="${metadataY + 56}" fill="#0f172a" font-family="Inter, Arial, sans-serif" font-size="28" font-weight="500">${categoryLabel}</text>
      ${showPlacement ? `
      <rect x="${placementPillX}" y="${metadataY + 20}" width="${placementPillWidth}" height="56" rx="28" fill="${placement.exportBadgeFill}" />
      <g transform="translate(${placementPillX + 16} ${metadataY + 36})" fill="none" stroke="${placement.exportIconColor}" color="${placement.exportIconColor}" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" aria-label="${placementLabel}">
        ${placement.svgMarkup}
      </g>
      <text x="${placementPillX + 38}" y="${metadataY + 56}" fill="#0f172a" font-family="Inter, Arial, sans-serif" font-size="24" font-weight="500">${placementLabel}</text>
      ` : ""}
      <text x="34" y="${tagIdY}" fill="#0f172a" font-family="ui-monospace, SFMono-Regular, Menlo, monospace" font-size="${STICKER_EXPORT_TAG_ID_FONT_SIZE_PX}" font-weight="600">${tagId}</text>
      <text x="34" y="${hostY}" fill="${theme.qrColor}" font-family="Inter, Arial, sans-serif" font-size="${STICKER_EXPORT_HOST_FONT_SIZE_PX}" font-weight="500">${host}</text>
    </svg>
  `.trim()
}

function loadImageFromDataUrl(dataUrl: string) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    if (typeof window === "undefined" || typeof window.Image !== "function") {
      reject(new Error("Image rasterization is unavailable for sticker ZIP export"))
      return
    }

    const image = new window.Image()
    image.onload = () => resolve(image)
    image.onerror = () => reject(new Error("Unable to rasterize sticker artwork for ZIP export"))
    image.src = dataUrl
  })
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality: number) {
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(blob)
        return
      }

      reject(new Error("Canvas export did not produce a printable JPG asset"))
    }, type, quality)
  })
}

async function buildStickerJpegBytes(
  tag: B2cStockPhysicalTagRecord,
  category: StickerCategoryDescriptor
) {
  const svgMarkup = await buildStickerSvg(tag, category)
  const svgDataUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgMarkup)}`
  const image = await loadImageFromDataUrl(svgDataUrl)
  const canvas = document.createElement("canvas")
  canvas.width = STICKER_EXPORT_WIDTH_PX
  canvas.height = STICKER_EXPORT_HEIGHT_PX

  const context = canvas.getContext("2d")
  if (!context) {
    throw new Error("Canvas 2D export context is unavailable for sticker ZIP export")
  }

  context.fillStyle = "#ffffff"
  context.fillRect(0, 0, STICKER_EXPORT_WIDTH_PX, STICKER_EXPORT_HEIGHT_PX)
  context.drawImage(image, 0, 0, STICKER_EXPORT_WIDTH_PX, STICKER_EXPORT_HEIGHT_PX)

  const jpegBlob = await canvasToBlob(canvas, "image/jpeg", STICKER_EXPORT_JPEG_QUALITY)
  return new Uint8Array(await jpegBlob.arrayBuffer())
}

async function downloadBatchZip(batch: B2cStockBatchDetail) {
  const archiveName = `${sanitizeFileSegment(batch.batchCode)}-stickers.zip`
  const fileEntries = await Promise.all(
    batch.kits.flatMap((kit) =>
      kit.physicalTags.map(async (tag) => {
        const jpegBytes = await buildStickerJpegBytes(tag, getStickerCategory(batch, kit))
        const fileName = `${sanitizeFileSegment(batch.batchCode)}/${sanitizeFileSegment(kit.kitCode)}/${sanitizeFileSegment(tag.tag)}.jpg`

        return [fileName, jpegBytes] as const
      })
    )
  )

  const archive = zipSync(Object.fromEntries(fileEntries), {
    level: 6,
  })
  const archiveBuffer = new Uint8Array(archive).buffer

  downloadBlob(new Blob([archiveBuffer], { type: "application/zip" }), archiveName)
}

function loadQrCodeLibrary() {
  if (typeof window === "undefined") {
    return Promise.resolve()
  }

  if (typeof window.QRCode === "function") {
    return Promise.resolve()
  }

  if (qrCodeLibraryPromise) {
    return qrCodeLibraryPromise
  }

  qrCodeLibraryPromise = new Promise<void>((resolve, reject) => {
    const script = document.createElement("script")
    script.src = "/admin/vendor/qrcode.min.js"
    script.async = true
    script.onload = () => resolve()
    script.onerror = () => reject(new Error("Unable to load QRCode renderer"))
    document.head.appendChild(script)
  })

  return qrCodeLibraryPromise
}

function stretchQrMarkup(container: HTMLDivElement) {
  const rootNode = container.firstElementChild

  if (!(rootNode instanceof HTMLElement)) {
    return
  }

  rootNode.style.width = "100%"
  rootNode.style.height = "100%"
  rootNode.style.display = "flex"
  rootNode.style.alignItems = "center"
  rootNode.style.justifyContent = "center"

  for (const node of rootNode.querySelectorAll("img, canvas, table")) {
    if (node instanceof HTMLElement) {
      node.style.width = "100%"
      node.style.height = "100%"
      node.style.maxWidth = "100%"
      node.style.maxHeight = "100%"
      node.style.objectFit = "contain"
    }
  }
}

function PrintQrCode(props: { colorDark: string; frameClassName: string; tag: B2cStockPhysicalTagRecord }) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [renderError, setRenderError] = useState<string | null>(null)
  const qrSize = estimateQrPixelSize(props.tag.sizeMm)

  useEffect(() => {
    let isActive = true

    async function renderQr() {
      const container = containerRef.current

      if (!container || !props.tag.qrTargetUrl) {
        return
      }

      container.innerHTML = ""
      setRenderError(null)

      try {
        await loadQrCodeLibrary()

        if (!isActive || typeof window.QRCode !== "function") {
          return
        }

        container.innerHTML = ""
        new window.QRCode(container, {
          text: props.tag.qrTargetUrl,
          width: qrSize,
          height: qrSize,
          colorDark: props.colorDark,
          colorLight: "#ffffff",
          correctLevel: window.QRCode.CorrectLevel?.M,
        })
        stretchQrMarkup(container)
      } catch (error) {
        if (!isActive) {
          return
        }

        setRenderError(error instanceof Error ? error.message : "QR rendering failed")
      }
    }

    void renderQr()

    return () => {
      isActive = false
      if (containerRef.current) {
        containerRef.current.innerHTML = ""
      }
    }
  }, [props.colorDark, props.tag.qrTargetUrl, qrSize])

  return (
    <div
      className={`flex aspect-square w-full items-center justify-center rounded-[2rem] border bg-white p-4 shadow-[inset_0_0_0_1px_rgba(148,163,184,0.08)] ${props.frameClassName}`}
    >
      <div
        ref={containerRef}
        aria-label={`QR for ${props.tag.tag}`}
        className="flex h-full w-full items-center justify-center overflow-hidden rounded-[1.5rem]"
      />
      {renderError ? (
        <span className="text-center text-xs leading-5 text-muted-foreground">
          QR unavailable in this environment.
        </span>
      ) : null}
    </div>
  )
}

function PrintTagCard(props: {
  category: StickerCategoryDescriptor
  tag: B2cStockPhysicalTagRecord
}) {
  const theme = getStickerTheme(props.tag)
  const placement = getStickerPlacementDescriptor(props.tag, props.category)
  const showPlacement = hasDistinctPlacementDescriptor(props.category, placement)
  const host = getStickerFooterHost(props.tag)

  return (
    <article
      className={`kit-print-card flex flex-col rounded-[2rem] border bg-white p-5 shadow-[0_18px_50px_-28px_rgba(15,23,42,0.2)] ${theme.cardClass}`}
      style={{ minHeight: getTileMinHeight(props.tag.sizeMm) }}
    >
      <div className="flex items-center gap-2 text-slate-950">
        <span
          className={`inline-flex h-8 w-8 items-center justify-center rounded-full ${theme.iconClass}`}
        >
          <CircleHelp className="h-4 w-4" />
        </span>
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">
            SafetyQR Sticker
          </p>
          <h3 className={`text-2xl font-semibold tracking-[0.08em] ${theme.titleClass}`}>
            {props.tag.primaryCopy ?? "SCAN TO HELP"}
          </h3>
        </div>
      </div>

      <div className="mt-4 flex flex-1 items-center justify-center">
        <PrintQrCode
          colorDark={theme.qrColor}
          frameClassName={theme.qrFrameClass}
          tag={props.tag}
        />
      </div>

      <div className={`mt-4 rounded-[1.5rem] px-4 py-3 ${theme.footerClass}`}>
        <div className="flex items-center gap-3">
          <StickerCategoryBadge category={props.category} />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-xs font-semibold text-slate-700">{props.category.label}</p>
              {showPlacement ? <StickerPlacementBadge descriptor={placement} /> : null}
            </div>
            <p className="mt-2 break-all font-mono text-base font-semibold tracking-[0.02em] text-slate-950">
              {props.tag.tag}
            </p>
            <p className={`mt-1 text-[11px] font-medium ${theme.titleClass}`}>{host}</p>
          </div>
        </div>
      </div>
    </article>
  )
}

export function AdminStockPrintPreview({ search }: AdminStockPrintPreviewProps) {
  const [batch, setBatch] = useState<B2cStockBatchDetail | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [isExporting, setIsExporting] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const returnTo = search.returnTo || "/admin/stock-ops"
  const variant = useMemo(
    () => (batch?.kitVariant ? getSafetyQrVariant(batch.kitVariant) : null),
    [batch?.kitVariant]
  )

  const triggerPrint = useEffectEvent(() => {
    if (!search.autoPrint || !batch || batch.lifecycleState === "generated") {
      return
    }

    window.setTimeout(() => {
      window.print()
    }, 320)
  })

  useEffect(() => {
    let isActive = true

    async function load() {
      if (!search.batchId) {
        startTransition(() => {
          setBatch(null)
          setErrorMessage("Missing batchId in the print preview URL.")
          setIsLoading(false)
        })
        return
      }

      startTransition(() => {
        setIsLoading(true)
        setErrorMessage(null)
      })

      try {
        const response = await apiClient.getB2cStockBatch(search.batchId)

        if (!isActive) {
          return
        }

        startTransition(() => {
          setBatch(response.data ?? null)
          setIsLoading(false)
        })
      } catch (error) {
        if (!isActive) {
          return
        }

        startTransition(() => {
          setBatch(null)
          setErrorMessage(
            error instanceof Error
              ? error.message
              : "Failed to load the SafetyQR print preview."
          )
          setIsLoading(false)
        })
      }
    }

    void load()

    return () => {
      isActive = false
    }
  }, [search.batchId])

  useEffect(() => {
    triggerPrint()
  }, [batch, triggerPrint])

  async function handlePrintAction() {
    if (!batch) {
      return
    }

    setIsSubmitting(true)
    setErrorMessage(null)

    try {
      if (batch.lifecycleState === "generated") {
        await apiClient.markB2cStockBatchPrinted(batch.id)
        const refreshedBatch = await apiClient.getB2cStockBatch(batch.id)

        startTransition(() => {
          setBatch(refreshedBatch.data ?? batch)
        })
      }

      window.print()
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : "Failed to approve the SafetyQR print batch."
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  async function handleZipDownload() {
    if (!batch) {
      return
    }

    setIsExporting(true)
    setErrorMessage(null)

    try {
      await downloadBatchZip(batch)
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : "Failed to generate the sticker ZIP bundle."
      )
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <div className="print-preview-root">
      <style>{`
        @media print {
          body * {
            visibility: hidden !important;
          }

          .print-preview-root,
          .print-preview-root * {
            visibility: visible !important;
          }

          body {
            background: #ffffff !important;
          }

          .print-preview-root {
            position: absolute !important;
            inset: 0 !important;
            width: 100% !important;
            margin: 0 !important;
            padding: 0.18in !important;
            background: #ffffff !important;
          }

          .print-toolbar {
            display: none !important;
          }

          .print-summary,
          .kit-sheet-header {
            display: none !important;
          }

          .kit-sheet {
            border: 0 !important;
            box-shadow: none !important;
            background: transparent !important;
            padding: 0 !important;
            margin: 0 !important;
          }

          .print-sticker-grid {
            display: grid !important;
            grid-template-columns: repeat(auto-fit, minmax(2.7in, 1fr)) !important;
            gap: 0.18in !important;
            align-items: start !important;
            margin: 0 !important;
          }

          .kit-print-card {
            break-inside: avoid;
            page-break-inside: avoid;
            box-shadow: none !important;
            min-height: auto !important;
            padding: 0.14in !important;
          }
        }
      `}</style>

      <div className="print-toolbar flex flex-wrap gap-3">
        <Button asChild variant="outline">
          <a href={returnTo}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to stock ops
          </a>
        </Button>
        <Button disabled={!batch || isSubmitting} type="button" onClick={handlePrintAction}>
          <Printer className="mr-2 h-4 w-4" />
          {isSubmitting
            ? "Approving…"
            : batch?.lifecycleState === "generated"
              ? "Approve and print"
              : "Print sheet"}
        </Button>
        <Button
          disabled={!batch || isSubmitting || isExporting}
          type="button"
          variant="outline"
          onClick={handleZipDownload}
        >
          {isExporting ? "Preparing ZIP…" : "Download ZIP"}
        </Button>
      </div>

      {errorMessage ? (
        <OperatorStatusMessage tone="danger">{errorMessage}</OperatorStatusMessage>
      ) : null}

      {batch?.lifecycleState === "generated" ? (
        <OperatorStatusMessage tone="warning">
          Review QR coverage, each sticker header, and each sticker ID before approving the print
          batch. The lifecycle stays generated until you approve and print from this screen.
        </OperatorStatusMessage>
      ) : null}

      {isLoading ? <PrintPreviewLoadingState /> : null}

      {!isLoading && batch ? (
        <>
          <section className="print-summary rounded-[2rem] border border-border/70 bg-[linear-gradient(135deg,#f8fafc_0%,#e2e8f0_100%)] p-6 shadow-[0_24px_70px_-40px_rgba(15,23,42,0.55)]">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="max-w-3xl">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-full bg-slate-950 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-white">
                    {getVariantLabel(batch)}
                  </span>
                  <span className="rounded-full bg-white/85 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-700">
                    {titleizeToken(batch.lifecycleState)}
                  </span>
                </div>
                <h1 className="mt-4 text-3xl font-semibold tracking-tight text-slate-950">
                  {batch.batchCode}
                </h1>
                <p className="mt-3 max-w-3xl text-sm leading-7 text-slate-700">
                  {batch.notes ||
                    "Review the kit print layout, identifiers, and QR targets before approving the batch."}
                </p>
              </div>
              <div className="rounded-[1.75rem] border border-white/70 bg-white/80 px-4 py-4 text-sm shadow-sm">
                <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                  Organization
                </div>
                <div className="mt-1 font-medium text-slate-950">
                  {batch.organization?.name || "Portal scope"}
                </div>
              </div>
            </div>

            <div className="mt-6 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              <div className="rounded-[1.5rem] border border-border/60 bg-white/85 px-4 py-3">
                <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
                  Kits
                </div>
                <div className="mt-1 text-2xl font-semibold text-slate-950">
                  {batch.quantityKits ?? batch.kitCount ?? batch.kits.length}
                </div>
              </div>
              <div className="rounded-[1.5rem] border border-border/60 bg-white/85 px-4 py-3">
                <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
                  Physical tags
                </div>
                <div className="mt-1 text-2xl font-semibold text-slate-950">
                  {batch.tagCount ?? batch.items.length}
                </div>
              </div>
              <div className="rounded-[1.5rem] border border-border/60 bg-white/85 px-4 py-3">
                <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
                  Printed
                </div>
                <div className="mt-1 text-sm font-medium text-slate-950">
                  {formatDateTime(batch.printedAt)}
                </div>
              </div>
              <div className="rounded-[1.5rem] border border-border/60 bg-white/85 px-4 py-3">
                <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
                  Catalog note
                </div>
                <div className="mt-1 text-sm font-medium leading-6 text-slate-950">
                  {variant?.launchGate.note ?? "No launch-gate note recorded."}
                </div>
              </div>
            </div>
          </section>

          <div className="grid gap-6">
            {batch.kits.map((kit) => {
              const category = getStickerCategory(batch, kit)

              return (
                <section
                  key={kit.id}
                  className="kit-sheet rounded-[2rem] border border-border/70 bg-[linear-gradient(180deg,#ffffff_0%,#f8fafc_100%)] p-5 shadow-[0_18px_60px_-38px_rgba(15,23,42,0.5)]"
                >
                <div className="kit-sheet-header flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="rounded-full bg-slate-100 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-700">
                        Kit {String(kit.sequenceNumber).padStart(3, "0")}
                      </span>
                      <span className="rounded-full bg-primary/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-primary">
                        {kit.kitCode}
                      </span>
                    </div>
                    <h2 className="mt-3 text-xl font-semibold tracking-tight text-slate-950">
                      {getVariantLabel(batch)}
                    </h2>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {titleizeToken(kit.vehicleClass ?? kit.solutionCategory ?? batch.productFamily ?? "kit")}
                      {" · "}
                      {titleizeToken(kit.lifecycleState ?? batch.lifecycleState)}
                    </p>
                  </div>
                  <div className="rounded-[1.5rem] border border-border/70 bg-slate-50 px-4 py-3 text-sm">
                    <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
                      Tag roles
                    </div>
                    <div className="mt-1 font-medium text-slate-950">
                      {kit.physicalTags.map((tag) => titleizeToken(tag.role ?? "unknown")).join(" · ")}
                    </div>
                  </div>
                </div>

                <div className="print-sticker-grid mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                  {kit.physicalTags.map((tag) => (
                    <PrintTagCard category={category} key={tag.tag} tag={tag} />
                  ))}
                </div>
                </section>
              )
            })}
          </div>
        </>
      ) : null}
    </div>
  )
}

function PrintPreviewLoadingState() {
  return (
    <>
      <section className="rounded-[2rem] border border-border/70 bg-[linear-gradient(135deg,#f8fafc_0%,#e2e8f0_100%)] p-6 shadow-[0_24px_70px_-40px_rgba(15,23,42,0.55)]">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="max-w-3xl flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <Skeleton className="h-7 w-36 rounded-full bg-white/85" />
              <Skeleton className="h-7 w-28 rounded-full bg-white/75" />
            </div>
            <Skeleton className="mt-4 h-10 w-56 bg-white/85" />
            <Skeleton className="mt-3 h-4 w-full bg-white/75" />
            <Skeleton className="mt-2 h-4 w-5/6 bg-white/70" />
          </div>
          <div className="rounded-[1.75rem] border border-white/70 bg-white/80 px-4 py-4 text-sm shadow-sm">
            <Skeleton className="h-3 w-20 bg-slate-200" />
            <Skeleton className="mt-2 h-5 w-32 bg-slate-200" />
          </div>
        </div>

        <div className="mt-6 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          {Array.from({ length: 4 }).map((_, index) => (
            <div
              key={`print-preview-summary-${index}`}
              className="rounded-[1.5rem] border border-border/60 bg-white/85 px-4 py-3"
            >
              <Skeleton className="h-3 w-16 bg-slate-200" />
              <Skeleton className="mt-2 h-6 w-24 bg-slate-200" />
              <Skeleton className="mt-2 h-4 w-full bg-slate-200" />
            </div>
          ))}
        </div>
      </section>

      <div className="grid gap-6">
        {Array.from({ length: 2 }).map((_, kitIndex) => (
          <section
            key={`print-preview-kit-${kitIndex}`}
            className="kit-sheet rounded-[2rem] border border-border/70 bg-[linear-gradient(180deg,#ffffff_0%,#f8fafc_100%)] p-5 shadow-[0_18px_60px_-38px_rgba(15,23,42,0.5)]"
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <Skeleton className="h-6 w-20 rounded-full" />
                  <Skeleton className="h-6 w-28 rounded-full" />
                </div>
                <Skeleton className="mt-3 h-7 w-48" />
                <Skeleton className="mt-2 h-4 w-40" />
              </div>
              <div className="rounded-[1.5rem] border border-border/70 bg-slate-50 px-4 py-3 text-sm">
                <Skeleton className="h-3 w-16 bg-slate-200" />
                <Skeleton className="mt-2 h-4 w-32 bg-slate-200" />
              </div>
            </div>

            <div className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {Array.from({ length: 3 }).map((_, tagIndex) => (
                <div
                  key={`print-preview-tag-${kitIndex}-${tagIndex}`}
                  className="kit-print-card rounded-[1.75rem] border border-border/70 bg-white/85 p-4"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <Skeleton className="h-5 w-32" />
                      <Skeleton className="mt-2 h-4 w-full" />
                      <Skeleton className="mt-2 h-4 w-4/5" />
                    </div>
                    <Skeleton className="h-16 w-16 rounded-xl" />
                  </div>
                  <Skeleton className="mt-4 h-24 w-full rounded-2xl" />
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>
    </>
  )
}
